import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribes to a CSS media query.
 *
 * `useSyncExternalStore` rather than useState + useEffect: it reads the correct
 * value during the first render, so there is no flash of the wrong layout, and
 * it stays consistent under concurrent rendering.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => undefined;
      }

      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** Breakpoint below which the documents grid is replaced by a card list. */
export const NARROW_QUERY = '(max-width: 720px)';

/** Breakpoint below which lower-value grid columns are dropped. */
export const COMPACT_QUERY = '(max-width: 1100px)';

export const usePrefersDark = () => useMediaQuery('(prefers-color-scheme: dark)');
export const useIsNarrow = () => useMediaQuery(NARROW_QUERY);
export const useIsCompact = () => useMediaQuery(COMPACT_QUERY);
